import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import "./lesson-details-content.component.scoped.scss";
import { useTranslation } from "react-i18next";
import { formatTime } from "../../../../../helpers";
import { services } from "../../../../../services";
import { Box, Typography, LinearProgress } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { icons } from "../../../../../statics/icons";
import ReactPlayer from "react-player/vimeo";
import { GlobalContext } from "../../../../../store";
import { UserRoles } from "../../../../../types/userRoles";
import StudentsComponent from "../../../shared/students/students.component";
import { BreadcrumbComponent } from "../../../../../components/breadcrumb/breadcrumb.component";
import { educationRoutes } from "../../../types/education.routes";
import { useLessonProgress } from "../../../hooks/useLessonProgress";
import LessonDetailsHeaderComponent from "../lesson-details-header/lesson-details-header.component";
import LessonDetailsStatisticsComponent from "../lesson-details-statistics/lesson-details-statistics.component";
import SectionNavigationComponent from "../section-navigation/section-navigation.component";
import { history } from "react-router-guard";

const LessonDetailsContentComponent = ({ lessonId }: { lessonId: string }) => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);
  const [lessonDetails, setLessonDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [lessonStudents, setLessonStudents] = useState<any[]>([]);

  const shouldTrackPlayback = useMemo(() => {
    const role = user?.role?.toUpperCase() ?? "";
    return role !== UserRoles.SUPER_ADMIN && role !== UserRoles.COORDINATOR;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleLessonUpdate = useCallback((data: Record<string, unknown>) => {
    setLessonDetails((prev: any) => ({
      ...prev,
      ...data,
      userStatus: {
        ...prev?.userStatus,
        ...(data.userStatus as object),
      },
    }));
  }, []);

  const {
    playerRef,
    playing,
    played,
    playedSeconds,
    duration,
    hasStartedPlaying,
    handlePlay,
    handlePause,
    handleProgress,
    handleDuration,
  } = useLessonProgress({
    lessonId,
    lesson: lessonDetails,
    enabled: shouldTrackPlayback,
    onLessonUpdate: handleLessonUpdate,
  });

  const displayDuration = formatTime(lessonDetails?.duration || duration);
  const totalDuration = lessonDetails?.duration || duration;

  const fetchLessonDetails = useCallback(async () => {
    try {
      setLoading(true);
      const lesson = await services.courses.getLesson(lessonId);
      setLessonDetails(lesson);
      services.common.setTitle(
        `${lesson?.title} - ${t("PAGES_HEAD.VIDEO_DETAILS")}`,
      );
    } catch (error) {
      console.error("Error fetching lesson details:", error);
      setLessonDetails(null);
      history.push(educationRoutes.courses);
    } finally {
      setLoading(false);
    }
  }, [lessonId, t]);

  useEffect(() => {
    fetchLessonDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  /**
   * Check if video URL is valid
   */
  const isValidVideoUrl = useCallback((url: string): boolean => {
    if (!url) return false;
    try {
      // Check if it's a valid URL
      new URL(url);
      // Check if it's a Vimeo URL or other supported video URL
      if (ReactPlayer && typeof ReactPlayer.canPlay === "function") {
        return ReactPlayer.canPlay(url);
      }
      // Fallback: check if URL contains common video platform patterns
      return /(vimeo|youtube|youtu\.be|dailymotion|cloudinary)/i.test(url);
    } catch {
      return false;
    }
  }, []);

  /**
   * Handle seek to position
   */
  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!playerRef.current || !totalDuration) {
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = x / rect.width;
      const seekTo = percent * totalDuration;
      playerRef.current.seekTo(seekTo, "seconds");
    },
    [totalDuration, playerRef],
  );

  // lesson students
  const fetchStudents = useCallback(
    async (params?: any) => {
      const students = await services.courses.lessonStudents(lessonId, params);
      setLessonStudents(students);
    },
    [lessonId],
  );

  useEffect(() => {
    fetchStudents();
  }, [lessonId, fetchStudents]);

  const onStudentsFilterChange = useCallback(
    (filters: any) => {
      fetchStudents(filters);
    },
    [fetchStudents],
  );

  const hasPreviewImage = Boolean(
    lessonDetails?.imageKey && lessonDetails?.imageURL,
  );

  const showPreview = hasPreviewImage && !hasStartedPlaying;
  const showPlayer =
    isValidVideoUrl(lessonDetails?.videoUrl) &&
    (hasStartedPlaying || !hasPreviewImage);

  return (
    <Box className="lesson__content mb-5">
      {loading ? (
        <Skeleton variant="text" height={18} width={350} className="my-4" />
      ) : (
        <BreadcrumbComponent
          items={[
            {
              label: t("EDUCATION_PAGE.EDUCATION"),
              path: "/education",
              separator: "-",
            },
            {
              label: lessonDetails?.course?.title,
              path: educationRoutes.course(lessonDetails?.course?.id),
              separator: "-",
            },
            { label: lessonDetails?.title },
          ]}
        />
      )}

      <Box className="global__box__content mb-4" style={{ padding: "2rem" }}>
        <LessonDetailsHeaderComponent
          duration={displayDuration}
          lessonId={lessonId}
          lessonDetails={lessonDetails}
          loading={loading}
          onEdit={fetchLessonDetails}
        />
        {/* Lesson Video */}
        <Box className="lesson__video__content">
          {loading ? (
            <Skeleton
              variant="rect"
              height={740}
              width="100%"
              style={{ borderRadius: "12px" }}
            />
          ) : (
            <Box className="lesson__video__container">
              {showPreview && (
                <Box
                  className="lesson__video__preview"
                  onClick={handlePlay}
                  style={{
                    position: "relative",
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={lessonDetails.imageURL}
                    alt={lessonDetails.title}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "740px",
                      objectFit: "cover",
                      borderRadius: "12px",
                    }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <img src={icons.PAGES.EDUCATION.PLAYER} alt="player" />
                  </Box>
                </Box>
              )}

              {showPlayer && (
                <Box
                  className="lesson__video__player"
                  style={{
                    display: playing || playedSeconds > 0 ? "block" : "none",
                    width: "100%",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <ReactPlayer
                    ref={playerRef}
                    // url={`${lessonDetails.videoUrl}?background=1&title=0&byline=0&portrait=0`}
                    url={lessonDetails.videoUrl}
                    playing={playing}
                    controls={true}
                    width="100%"
                    height="740px"
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onProgress={(event: any) =>
                      handleProgress({
                        played: event.played,
                        playedSeconds: event.playedSeconds,
                      })
                    }
                    onDuration={handleDuration}
                    config={{
                      playerOptions: {
                        playsinline: true,
                      },
                    }}
                  />
                </Box>
              )}

              {!isValidVideoUrl(lessonDetails?.videoUrl) && !loading && (
                <Box
                  style={{
                    padding: "2rem",
                    textAlign: "center",
                    backgroundColor: "var(--light)",
                    borderRadius: "8px",
                  }}
                >
                  <Typography variant="body1" color="error">
                    {t("EDUCATION_PAGE.INVALID_VIDEO_URL") ||
                      "Invalid or missing video URL"}
                  </Typography>
                </Box>
              )}

              {/* Progress Bar Section */}
              {totalDuration > 0 && (
                <Box
                  className="lesson__video__progress"
                  style={{
                    width: "100%",
                    margin: "auto",
                    marginTop: "1.9rem",
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography variant="caption" style={{ color: "#78829D" }}>
                      {formatTime(playedSeconds)} / {formatTime(totalDuration)}
                    </Typography>
                    <Typography variant="caption" style={{ color: "#78829D" }}>
                      {Math.round(played * 100)}%
                    </Typography>
                  </Box>
                  <Box
                    onClick={handleSeek}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "8px",
                      backgroundColor: "var(--light)",
                      borderRadius: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={played * 100}
                      style={{
                        height: "8px",
                        borderRadius: "12px",
                        backgroundColor: "transparent",
                      }}
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "8px",
                    }}
                  >
                    <Typography variant="caption" style={{ color: "#78829D" }}>
                      {t("EDUCATION_PAGE.WATCHED")}: {formatTime(playedSeconds)}{" "}
                      {t("EDUCATION_PAGE.MIN")}
                    </Typography>
                    <Typography variant="caption" style={{ color: "#78829D" }}>
                      {t("EDUCATION_PAGE.REMAINING")}:{" "}
                      {formatTime(Math.max(0, totalDuration - playedSeconds))}{" "}
                      {t("EDUCATION_PAGE.MIN")}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {lessonDetails?.sectionId && (
        <SectionNavigationComponent
          lessonDetails={lessonDetails}
          lessonId={lessonId}
          loading={loading}
        />
      )}

      <LessonDetailsStatisticsComponent
        lessonDetails={lessonDetails}
        duration={displayDuration}
      />

      {UserRoles.FULL_ACCESS_ROLES.includes(user.role) && (
        <Box className="mt-4">
          <StudentsComponent
            title={t("EDUCATION_PAGE.VIDEO_MEMBERS_FINISHED")}
            users={lessonStudents}
            loading={loading}
            onFilterChange={onStudentsFilterChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default LessonDetailsContentComponent;
